import { Primary, SvgIcon, Text, designTokens } from '@cinch-labs/design-system'
import {
  returnToFinanceApplicationCheckoutStage,
  useViewport,
} from '@cinch-labs/shared-util'
import cx from 'classnames'
import { useCallback, useState } from 'react'
import type { DigitalDataEvent, VehicleOrdersRequest } from '../types'
import { getBasketBannerClosed, setBasketBannerClosed } from '../utils'
import { useFinanceOfferOrder } from '../hooks/use-finance-offer-order'
import BasketBannerVehicleInformation from '../vehicle-information/vehicle-information'
import {
  basketBannerCloseBannerEvent,
  basketBannerReturnToFinanceCheckoutEvent,
} from './banner-analytics'
import styles from './banner.module.css'

export interface FinanceBannerProps {
  sendDigitalDataEvent: (event: DigitalDataEvent) => void
  locale?: {
    financeOrderActive: {
      buttonText: string
    }
  }
  vehicleOrder: VehicleOrdersRequest | undefined
  adminFeeFlag?: boolean
  adminFeeValueInPence?: number
  finAppCheckoutBannerFlag?: boolean
}
export function FinanceBanner({
  locale = {
    financeOrderActive: {
      buttonText: 'Review your offer',
    },
  },
  sendDigitalDataEvent,
  adminFeeFlag = false,
  adminFeeValueInPence,
  finAppCheckoutBannerFlag,
  vehicleOrder,
}: FinanceBannerProps) {
  const basketBannerState = getBasketBannerClosed()
  const [hideBasketBanner, setHideBasketBanner] = useState(basketBannerState)

  const { deepPurple } = designTokens.customProperties.ds.color

  const { vw } = useViewport({
    updateOnResize: true,
    updateOnOrientationChange: true,
  })

  const isMobile = vw < 768

  const handleCloseBanner = useCallback(() => {
    setHideBasketBanner(true)

    setBasketBannerClosed(true)

    sendDigitalDataEvent(basketBannerCloseBannerEvent())
  }, [])

  const liveFinanceVehicle = useFinanceOfferOrder(vehicleOrder)

  const shouldShowFinanceBanner = finAppCheckoutBannerFlag && liveFinanceVehicle
  const isNewCarOrder = liveFinanceVehicle?.vehicle?.condition === 'new'

  const handleReturnToCheckout = useCallback(async () => {
    const orderId = liveFinanceVehicle?.orderId
    const financeId = liveFinanceVehicle?.financeId

    if (!vehicleOrder || !orderId || !financeId) {
      return
    }

    returnToFinanceApplicationCheckoutStage(orderId, financeId, isNewCarOrder)
    sendDigitalDataEvent(basketBannerReturnToFinanceCheckoutEvent())
  }, [vehicleOrder, liveFinanceVehicle, isNewCarOrder])

  return shouldShowFinanceBanner ? (
    <div
      className={cx(styles['banner'], {
        [styles['hideBasketBanner']]: hideBasketBanner,
      })}
    >
      <section
        role="dialog"
        aria-labelledby="finance-offer-banner"
        aria-describedby="description-of-finance-offer-vehicle"
        aria-hidden={hideBasketBanner}
        className={styles['bannerSection']}
      >
        {isMobile ? (
          <>
            <Text className={styles['bannerHeading']} id="finance-offer-banner">
              You've got a finance offer!
            </Text>

            <button
              aria-label="Close Active Order Banner"
              className={styles['closeIconButton']}
              onClick={() => handleCloseBanner()}
            >
              <SvgIcon
                name="cross"
                height={12}
                width={12}
                stroke={deepPurple}
                strokeWidth="2"
              />
            </button>

            <BasketBannerVehicleInformation
              sendDigitalDataEvent={sendDigitalDataEvent}
              vehicle={liveFinanceVehicle.vehicle}
              adminFeeFlag={adminFeeFlag}
              adminFeeValueInPence={adminFeeValueInPence}
              isFinanceOrder={finAppCheckoutBannerFlag}
            />
            <div className={styles.buttonWrapper}>
              <Primary
                classNames={{ button: styles['returnToCheckoutCta'] }}
                disabled={false}
                onClick={() => handleReturnToCheckout()}
                size="large"
              >
                {locale.financeOrderActive.buttonText}
              </Primary>
            </div>
          </>
        ) : (
          <div className={styles['basketBannerWrapper']}>
            <button
              aria-label="Close Active Order Banner"
              data-testid="closeBanner"
              className={styles['closeIconButton']}
              onClick={() => handleCloseBanner()}
            >
              <SvgIcon
                name="cross"
                height={12}
                width={12}
                stroke={deepPurple}
                strokeWidth="2"
              />
            </button>
            <BasketBannerVehicleInformation
              sendDigitalDataEvent={sendDigitalDataEvent}
              vehicle={liveFinanceVehicle.vehicle}
              adminFeeFlag={adminFeeFlag}
              adminFeeValueInPence={adminFeeValueInPence}
              isFinanceOrder={finAppCheckoutBannerFlag}
            />

            <div className={styles.buttonWrapper}>
              <Primary
                classNames={{ button: styles['returnToCheckoutCta'] }}
                disabled={false}
                onClick={() => handleReturnToCheckout()}
                size="large"
                data-testid="returnToReviewOrder"
              >
                {locale.financeOrderActive.buttonText}
              </Primary>
            </div>
          </div>
        )}
      </section>
    </div>
  ) : null
}

export default FinanceBanner
